import { Generator, Resource } from '../../types/game-types';
import { Icon } from '../Icon';
import styles from './styles.module.scss';
import { useGameStatusStore } from '../../store/zustand/useGameStatusStore';
import { useCurrencyStore } from '../../store/zustand/useCurrencyStore';
import { useTranslation } from 'react-i18next';
import React, { useRef } from 'react';
import { humanNumber } from '@game-libs/exc-libs/src/helpers';
import { useParentHover } from '@game-libs/exc-libs/src/hooks';
import { useItemCost } from '../../store/hooks/useItemCost';
import { Tooltip } from 'radix-ui';
import { UnitDescription } from '../components/UnitDescription';

export const ItemInfo = ({ item, children }: { item: Generator; children?: React.ReactNode }) => {
  const buyMax = useGameStatusStore((gameStatusState) => gameStatusState.buyMax);
  // Get currency values from the currency store for cost display
  const gold = useCurrencyStore((state) => state.gold);
  const mana = useCurrencyStore((state) => state.mana);
  const wood = useCurrencyStore((state) => state.wood);
  const ore = useCurrencyStore((state) => state.ore);
  const food = useCurrencyStore((state) => state.food);
  const state = { gold, mana, wood, ore, food };
  const stunEffectTickDurationRemaining = useGameStatusStore((s) => s.stunEffectTickDurationRemaining);
  const { t } = useTranslation('generators');
  const ref = useRef<HTMLDivElement>(null);
  const parentHovered = useParentHover(ref);

  const [cost, amountToBuy] = useItemCost(item);

  const unitDescriptionKey = `unitDescription.${item.id}`;
  const unitDescription = t(unitDescriptionKey, {});
  const hasDescription = unitDescription && unitDescription !== unitDescriptionKey;

  return (
    <div ref={ref} className={`${styles.ItemInfo} ${parentHovered ? styles.hovered : ''} ${styles[item.type]} `}>
      {hasDescription ? (
        <Tooltip.Provider>
          <Tooltip.Root disableHoverableContent delayDuration={200}>
            <Tooltip.Trigger>
              <div
                style={
                  {
                    '--buttonImageUrl': `url('../ui/buttons/${item.id}.png')`,
                  } as React.CSSProperties
                }
                className={`${styles.generatorImage} ${styles[item.type]} ${stunEffectTickDurationRemaining > 0 ? styles.stunned : ''
                  }`}
              ></div>
            </Tooltip.Trigger>
            <Tooltip.Portal container={document.getElementById('ui-wrapper')}>
              <Tooltip.Content style={{ zIndex: '1' }}>
                <UnitDescription>{unitDescription}</UnitDescription>
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      ) : (
        <div
          style={
            {
              '--buttonImageUrl': `url('../ui/buttons/${item.id}.png')`,
            } as React.CSSProperties
          }
          className={`${styles.generatorImage} ${styles[item.type]} ${stunEffectTickDurationRemaining > 0 ? styles.stunned : ''
            }`}
        ></div>
      )}
      <div className={styles.upgradeInfo}>
        {children}
        <div className={`${styles.costList}`}>
          {Object.entries(cost).map(([resource, cost]) => (
            <div key={resource} className={styles.generatorCost}>
              <Icon iconId={'item_cost_' + resource} />
              <div className={`${styles.generatorPrice} ${state[resource as Resource] < cost ? styles.disabled : ''}`}>
                <div>{humanNumber(cost, 1)}</div>
              </div>
            </div>
          ))}
          <span className={styles.buyMaxAmount}>
            {buyMax &&
              `(${t('buy', {
                amount: humanNumber(amountToBuy),
              })})`}
          </span>
        </div>
      </div>
    </div>
  );
};
